import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Zap, ShieldCheck, Search, Brain, Cpu, Send } from "lucide-react";
import { MarketingNav, MarketingFooter } from "@/components/TailorWizMarketingHome";

const BENEFITS = [
  {
    icon: Zap,
    kicker: "Speed",
    title: "Tailored in Under 60 Seconds. Not 60 Hours.",
    body: "Manual tailoring takes most people 2 to 4 hours per application. TailorWiz does it in under 60 seconds with a 90% or better match score. That is the difference between applying to 5 jobs a week and applying to 25 of the right ones, all properly positioned.",
  },
  {
    icon: ShieldCheck,
    kicker: "Honesty",
    title: "Built on Honesty. Not Fabrication.",
    body: "For a $50K role, exaggeration might go unnoticed. For a $350K executive placement, it will be fact-checked in reference calls, competency-based interviews, and background verification. TailorWiz tailors only from what your verified background honestly supports — and flags anything it cannot back up. Everything in your documents reflects what you have actually done, repositioned at the highest level. Your executive reputation is never put at risk.",
  },
  {
    icon: Search,
    kicker: "Intelligence",
    title: "It Knows the Company. Not Just the Posting.",
    body: "Every other AI tool reads the job description. So does every other candidate. TailorWiz reads the company. Through JobIntel 360 it scans 6,000+ verified sources to find the real problem the team is under pressure to solve, the strategic shift the new VP is driving, the priority the CEO mentioned on the last earnings call — then builds those insights into your documents.",
  },
  {
    icon: Brain,
    kicker: "Authority",
    title: "Built by a World Renowned Executive Resume Writer.",
    body: "ChatGPT was trained on the internet. It knows a little about everything and a lot about nothing at a specialist level. Marcus Hall has spent 14 years in the trenches of executive career strategy. TailorWiz was trained on his methodology — it thinks the way Marcus thinks, positions candidates the way Marcus positions them, and knows the difference between a bullet that sounds impressive and one that makes a hiring manager pick up the phone.",
  },
  {
    icon: Cpu,
    kicker: "Algorithm-Proof",
    title: "Built for the 2026 ATS. Not the 2018 One.",
    body: "The algorithms that filter resumes before a human ever sees them have evolved significantly. Keyword density, simple formatting, and predictable section headers are no longer sufficient. TailorWiz was built with a deep and current understanding of how the 2026 ATS systems actually evaluate resumes — semantic relevance, modern parsing, section weighting. Everything ChatGPT still does not know about.",
  },
  {
    icon: Send,
    kicker: "Bypass the Gatekeepers",
    title: "Land in the Decision Maker's Inbox. Not the Database.",
    body: "80% or more of qualified executive applicants get killed by ATS and AI filters before anyone with hiring power ever sees them. TailorWiz finishes the job every other tool quits on — it helps you identify the actual decision maker, source their direct contact, and arms you with an Outreach Letter built around verified company intelligence that lands your documents in the inbox of the only person with the power to call you in.",
  },
];

const COMPARISON = [
  {
    name: "ChatGPT / Generic AI",
    desc: "Reads the job description. Mirrors keywords. Produces a resume that looks like the other 200 in the pile.",
    rate: "2–5%",
    win: false,
  },
  {
    name: "Resume Templates",
    desc: "Pretty layout, generic content. Looks identical to thousands of other candidates using the same template. Tells the ATS nothing useful.",
    rate: "1–3%",
    win: false,
  },
  {
    name: "Resume Writers ($500–$3,000)",
    desc: "One static resume that does not adapt to each job. Two-week turnaround. Quality varies widely by writer.",
    rate: "5–15%",
    win: false,
  },
  {
    name: "TailorWiz",
    desc: "Built on Marcus Hall's 14+ years. Powered by JobIntel 360. Optimized for the 2026 ATS. Honest. Direct to the decision maker. Tailored in 60 seconds.",
    rate: "35–45%",
    win: true,
  },
];

export default function Benefits() {
  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <MarketingNav />

      {/* HERO */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#0B1C3F] via-[#0E2350] to-[#0B1C3F] text-white">
        <div
          className="absolute inset-0 pointer-events-none opacity-[0.16]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 18% 25%, #C9A84C 0, transparent 38%), radial-gradient(circle at 82% 75%, #4A78C2 0, transparent 38%)",
          }}
        />
        <div className="relative max-w-3xl mx-auto px-6 pt-16 pb-14 sm:pt-20 sm:pb-16 text-center">
          <span className="inline-flex items-center gap-2 text-[11px] sm:text-[12px] font-bold uppercase tracking-[0.18em] text-[#C9A84C] bg-white/5 px-4 py-2 rounded-full border border-[#C9A84C]/40">
            <Sparkles className="w-3.5 h-3.5 text-[#C9A84C]" />
            Why TailorWiz
          </span>
          <h1 className="mt-6 text-[34px] sm:text-[48px] font-extrabold tracking-tight leading-[1.07]">
            Why TailorWiz Beats{" "}
            <span className="text-[#C9A84C]">ChatGPT, Templates &amp; $3,000 Writers</span>
          </h1>
          <p className="mt-5 text-[16px] sm:text-[18px] leading-[1.6] text-slate-200">
            Six reasons TailorWiz is not just another AI resume tool — it is the
            one built to actually get job seekers hired.
          </p>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6 space-y-5">
          {BENEFITS.map((b, i) => {
            const Icon = b.icon;
            return (
              <div
                key={b.kicker}
                className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-8 hover:border-[#C9A84C]/50 hover:shadow-lg transition-all flex gap-5"
              >
                <div className="flex-shrink-0">
                  <div className="w-14 h-14 rounded-xl bg-[#0B1C3F] flex items-center justify-center">
                    <Icon className="w-6 h-6 text-[#C9A84C]" />
                  </div>
                  <div className="mt-2 text-center text-[20px] font-extrabold text-slate-200">
                    0{i + 1}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-[12px] font-extrabold tracking-[0.15em] uppercase text-[#C9A84C]">
                    {b.kicker}
                  </span>
                  <h2 className="mt-1 text-[20px] sm:text-[24px] font-extrabold tracking-tight text-slate-900">
                    {b.title}
                  </h2>
                  <p className="mt-2.5 text-[15px] leading-[1.6] text-slate-600">{b.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* COMPARISON */}
      <section className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-center text-[28px] sm:text-[38px] font-extrabold tracking-tight text-slate-900">
            TailorWiz <span className="text-[#C9A84C]">vs. Everything Else</span>
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {COMPARISON.map((c) => (
              <div
                key={c.name}
                className={`rounded-2xl p-5 flex flex-col ${
                  c.win
                    ? "bg-[#0B1C3F] border-2 border-[#C9A84C] text-white"
                    : "bg-white border border-slate-200"
                }`}
              >
                <h3
                  className={`text-[15px] font-extrabold ${
                    c.win ? "text-[#C9A84C]" : "text-slate-900"
                  }`}
                >
                  {c.name}
                </h3>
                <p
                  className={`mt-2 text-[13px] leading-[1.5] flex-1 ${
                    c.win ? "text-slate-200" : "text-slate-600"
                  }`}
                >
                  {c.desc}
                </p>
                <div
                  className={`mt-4 pt-3 border-t ${
                    c.win ? "border-white/15" : "border-slate-100"
                  }`}
                >
                  <div
                    className={`text-[11px] uppercase tracking-wide font-bold ${
                      c.win ? "text-slate-300" : "text-slate-400"
                    }`}
                  >
                    Response Rate
                  </div>
                  <div
                    className={`text-[26px] font-extrabold ${
                      c.win ? "text-[#C9A84C]" : "text-slate-700"
                    }`}
                  >
                    {c.rate}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-20 sm:py-24 bg-gradient-to-b from-white to-slate-50 text-center">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-[30px] sm:text-[42px] font-extrabold tracking-tight text-slate-900 leading-[1.1]">
            See the Difference <span className="text-[#C9A84C]">For Yourself.</span>
          </h2>
          <p className="mt-5 text-[16px] sm:text-[18px] text-slate-600 leading-[1.6]">
            Run your resume through TailorWiz — memberships start at $19/month
            required. 90% match score guaranteed every run.
          </p>
          <div className="mt-8">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#C9A84C] text-[#0B1C3F] text-[16px] font-bold shadow-lg shadow-[#C9A84C]/30 hover:bg-[#E5C36A] transition-all"
            >
              Try It Out FREE Now <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
